import { CommonModule, NgFor, NgIf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatSelectModule } from '@angular/material/select';

import { Filters } from '../../../mock-data/mock-filters';
import { ActiveFilter } from '../../../interfaces/active-filter';
import { Filter } from '../../../interfaces/filter';
import {
  masterComponent,
  masterCountryCode,
  masterIssueType,
  masterpKeyword,
  masterSeverity,
  masterStatusGranulated,
  masterTicketType,
} from '../../../interfaces/masterData';
import { Product } from '../../../interfaces/pProduct';
import { globalUser } from '../../../helpers/globalFields';
import { MasterDataService } from '../../../services/master-data.service';
import { UserService } from '../../../services/user.service';
import { CdkOverlayOrigin, OverlayModule } from '@angular/cdk/overlay';
import { debounceTime, map, Subject, switchMap } from 'rxjs';
import { PTicketService } from '../../../services/p-ticket.service';
import { MatTooltipModule } from '@angular/material/tooltip';

interface DateRangeValue {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-p-ticket-filter',
  standalone: true,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'de' },
    provideMomentDateAdapter(),
  ],
  imports: [
    CommonModule,
    FormsModule,
    NgFor,
    NgIf,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    CdkOverlayOrigin,
    OverlayModule,
    MatTooltipModule
  ],
  templateUrl: './p-ticket-filter.component.html',
  styleUrl: './p-ticket-filter.component.scss',
})
export class PTicketFilterComponent implements OnInit {
  @Output() httpFilterList = new EventEmitter<HttpParams>();
  @Output() filtersChanged = new EventEmitter<ActiveFilter[]>();
  @Output() applied = new EventEmitter<void>();

  @Input() tableName!: string;
  @Input() filters: ActiveFilter[] = [];

  filterOptions = Filters.filter((f) => f.displayed);

  httpParams: HttpParams = new HttpParams();

  products: Product[] = [];
  tickettypes: masterTicketType[] = [];
  issuetypes: masterIssueType[] = [];
  severities: masterSeverity[] = [];
  components: masterComponent[] = [];
  statusGranulated: masterStatusGranulated[] = [];
  pkeywords: masterpKeyword[] = [];

  countryCodes: masterCountryCode[] = [];
  filteredcountryCodes: masterCountryCode[] = [];

  constructor(
    private masterDataService: MasterDataService,
    private pTicketService: PTicketService,
    private userService: UserService,
    private cdRef: ChangeDetectorRef,
  ) { }

  private valueSearchSubject = new Subject<{
    item: any;
    dbKey: string;
    value: string;
  }>();

  ngOnInit(): void {
    // org-based filter options
    Filters.forEach((filter) => {
      if (filter.db_key === 'pct_organization') {
        filter.options = globalUser.getUser().organizations;
      }
    });

    // IMPORTANT: restore date objects (JSON -> DatePicker fix)
    this.restoreDates();



    this.masterDataService.getMasterCountrycodeMockList().subscribe((c) => {
      this.countryCodes = c;
      this.filteredcountryCodes = c;
    });

    this.masterDataService.getMasterProductList().subscribe((p) => {
      this.products = p;
    });

    this.masterDataService.getMasterTicketTypeList().subscribe((t) => {
      this.tickettypes = t;
    });

    this.masterDataService.getMasterIssueTypeList().subscribe((i) => {
      this.issuetypes = i;
    });

    this.masterDataService.getMasterSeverityList().subscribe((s) => {
      this.severities = s;
    });

    this.masterDataService.getMasterComponentList().subscribe((s) => {
      this.components = s;
    });

    this.masterDataService.getMasterStatusGranulatedList().subscribe((s) => {
      this.statusGranulated = s;
    });

    this.masterDataService.getMasterpKeywordList().subscribe((s) => {
      this.pkeywords = s;
    });

    this.applyFiltersToUIUpdate();

    this.valueSearchSubject
      .pipe(
        debounceTime(300),
        switchMap(({ item, dbKey, value }) => {
          item._suggestionsLoading = true;
          const httpParams = new HttpParams().append(dbKey, value || '');
          return this.pTicketService
            .getAll(httpParams)
            .pipe(
              map((res: any) => ({ item, dbKey, data: res.data as any[] })),
            );
        }),
      )
      .subscribe(({ item, dbKey, data }) => {
        const values = Array.from(
          new Set(
            data
              .map((t) => t[dbKey])
              .filter((v) => v !== undefined && v !== null && v !== ''),
          ),
        );
        item._suggestions = values.slice(0, 20);
        item._suggestionsLoading = false;
      });
  }

  // -------------------------
  // FILTER EDITING
  // -------------------------

  applyFiltersToUIUpdate(): void {
    this.filters = [...this.filters];
    this.cdRef.detectChanges();
  }

  onFilterColumnChange(filter: ActiveFilter, newKey: Filter): void {
    filter.key = newKey;
    filter.value =
      newKey.filterType === 'dateRange' ? { start: null, end: null } : null;

    this.applyFiltersToUIUpdate();
  }

  addFilterRow(): void {
    this.filters.push({
      id: crypto.randomUUID(),
      key: null,
      value: null,
    });

    this.applyFiltersToUIUpdate();
  }

  onFilterValueChange(filter: ActiveFilter, value: any): void {
    filter.value = value;
  }

  removeFilter(filter: ActiveFilter): void {
    this.filters = this.filters.filter((f) => f !== filter);
    //this.apply();
    this.applyFiltersToUIUpdate();
  }

  removeAllFilters(): void {
    this.filters = [];
  }

  // -------------------------
  // APPLY + SAVE
  // -------------------------

  apply(): void {
    this.filtersChanged.emit(this.filters);
    this.buildHttpParams();
    this.saveToDb();

    this.filteredcountryCodes = this.countryCodes;
    this.applied.emit();
  }

  private buildHttpParams(): void {
    this.httpParams = new HttpParams();

    this.filters.forEach((f) => {
      if (!this.isValid(f) || !f.key) return;

      if (f.key.filterType === 'dateRange') {
        const r = f.value as DateRangeValue;

        this.httpParams = this.httpParams
          .append('updatedAt[$gte]', new Date(r.start!).toISOString())
          .append('updatedAt[$lte]', new Date(r.end!).toISOString());

        return;
      }

      this.httpParams = this.httpParams.append(f.key.db_key, f.value);
    });

    this.httpFilterList.emit(this.httpParams);
  }

  private saveToDb(): void {
    if (!this.tableName) return;

    const validFilters = this.filters.filter((f) => this.isValid(f));

    this.userService.saveTableFilters(this.tableName, validFilters).subscribe({
      error: (err) => console.error('Save failed:', err),
    });
  }

  private isValid(f: ActiveFilter): boolean {
    if (!f.key) return false;

    if (f.key.filterType === 'dateRange') {
      return !!f.value?.start && !!f.value?.end;
    }

    return f.value !== null && f.value !== undefined && f.value !== '';
  }

  // -------------------------
  // DATE RESTORE (IMPORTANT)
  // -------------------------

  private restoreDates(): void {
    this.filters.forEach((f) => {
      if (f.key?.filterType !== 'dateRange' || !f.value) return;

      f.value = {
        start: f.value.start ? new Date(f.value.start) : null,
        end: f.value.end ? new Date(f.value.end) : null,
      };
    });
  }

  // -------------------------
  // DATE RANGE <-> native <input type="date"> helpers
  // -------------------------

  /** Date -> "yyyy-MM-dd" for native date input value binding */
  toDateInputValue(d: Date | null | undefined): string {
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /** "yyyy-MM-dd" (native input's emitted string) -> Date, at local midnight */
  fromDateInputValue(value: string): Date | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // -------------------------
  // UI HELPERS
  // -------------------------

  filterKeywords(event: Event): void {
    const val = (event.target as HTMLInputElement).value.toLowerCase();

    this.filteredcountryCodes = this.countryCodes.filter(
      (c) =>
        c.cod_alpha2.toLowerCase().includes(val) ||
        c.cod_countryname.toLowerCase().includes(val) ||
        c.cod_countrycode.toLowerCase().includes(val),
    );
  }

  displayKeyFn = (filter: any) => filter?.display_key ?? '';
  displayCountryFn = (entry: any) =>
    `${entry.cod_alpha2} — ${entry.cod_countryname}`;

  get productOptionValues() {
    return this.products.map((p) => p.ppd_name);
  }
  get tickettypeOptionValues() {
    return this.tickettypes.map((t) => t.mtt_value);
  }
  get issuetypeOptionValues() {
    return this.issuetypes.map((i) => i.mit_value);
  }
  get severityOptionValues() {
    return this.severities.map((s) => s.msv_value);
  }

  get componentOptionValues() {
    return this.components.map((s) => s.mco_value);
  }

  get statusGranulatedOptionValues() {
    return this.statusGranulated.map((s) => s.msg_value);
  }

  get pKeywordOptionValues() {
    return this.pkeywords.map((s) => s.mpk_value);
  }
  makeColumnSelectHandler(item: any, onColumnChange: Function) {
    return (value: any) => onColumnChange(item, value);
  }
  makeValueSelectHandler(item: any, onValueChange: Function) {
    return (value: any) => onValueChange(item, value);
  }
  makeTogglePanel(item: any, panelKey: 'columnPanel' | 'valuePanel') {
    return () => {
      const flag =
        panelKey === 'columnPanel' ? '_columnPanelOpen' : '_valuePanelOpen';
      item[flag] = !item[flag];
    };
  }
  makeClosePanel(item: any, panelKey: 'columnPanel' | 'valuePanel') {
    return () => {
      const flag =
        panelKey === 'columnPanel' ? '_columnPanelOpen' : '_valuePanelOpen';
      item[flag] = false;
    };
  }

  makeOpenPanel(item: any, panelKey: 'columnPanel' | 'valuePanel') {
    return () => {
      const flag =
        panelKey === 'columnPanel' ? '_columnPanelOpen' : '_valuePanelOpen';
      item[flag] = true;
    };
  }

  // Country: typing filters `filteredcountryCodes` via your existing filterKeywords(),
  // and also writes the typed text straight into item.value (matches your original behavior).
  makeCountryInputHandler(item: any, onValueChange: Function) {
    return (value: string) => {
      onValueChange(item, value);
      this.filterKeywords({ target: { value } } as any); // reuse your existing filter method
      item._valuePanelOpen = true;
    };
  }
  makeCountrySelectHandler(item: any, onValueChange: Function) {
    return (entry: any) => onValueChange(item, entry.cod_alpha2);
  }

  // Generic textAutocomplete (user / useremail / company / callbacknumber)
  makeAutocompleteInputHandler(item: any, onValueChange: Function) {
    return (value: string) => {
      onValueChange(item, value);
      item._valuePanelOpen = true;
      this.valueSearchSubject.next({
        item,
        dbKey: item.key.db_key,
        value,
      });
    };
  }
}
